import React from "react";
import { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, Dropdown, Input } from "antd";
import { useToast } from "@chakra-ui/react";
import * as yup from "yup";
import { CloseOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import AddDocuments from "./AddDocuments";
import LinkDoc from "./LinkDocument";
import { UploadOutlined } from "@mui/icons-material";
import { useRef } from "react";
import { FileOpen } from "@mui/icons-material";
import { useAccessControl } from "../../hooks/useAccessControl";
import DocumentDetailsPanel from "./DocumentDetailsPanel";

const schema = yup.object().shape({
  name: yup.string().required("Drawing Number is required"),
  description: yup.string(),
  disciplineType: yup.mixed().required("Discipline is required"),
  deliverableType: yup.mixed().required("Document type is required"),
  revNumber: yup.string(),
});

const DocsModal = ({ object, objectType }) => {
  const api = useAxios();
  const toast = useToast();
  const [viewMode, setViewMode] = useState("grid"); // or "list" as the default
  let tapTimeout = null;
  const { Search } = Input;
  const { state } = useLocation();
  const { equipmentId, tierId } = state || {};

  const [selectedCard, setSelectedCard] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDocument, setOpenEditDocument] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [menuCard, setMenuCard] = useState(null);

  const [disciplineTypes, setDisciplineTypes] = useState([]);
  const [deliverableTypes, setDeliverableTypes] = useState([]);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(""); // Added state for file name
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [disciplineType, setDisciplineType] = useState("");
  const [deliverableType, setDeliverableType] = useState("");
  const [deliverableStatus, setDeliverableStatus] = useState("");
  const [revNumber, setRevNumber] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [delinking, setDelinking] = useState(null); // id of link being deleted
  const [linkedAssets, setLinkedAssets] = useState([]);
  const [linkDocCard, setLinkDocCard] = useState(null); // doc being linked to a new asset

  const fileInputRef = useRef(null);

  // State for Modal Open/Close
  const [open, setOpen] = React.useState(false);

  const {
    canEdit,
    canDelete,
    loading: permissionsLoading,
  } = useAccessControl(objectType, object.id, { enabled: open });
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const createDisciplineType = async (name) => {
    try {
      const response = await api.post("/discipline-types/", { name });
      const newType = response.data;
      setDisciplineTypes((prev) => [...prev, newType]);
      return newType;
    } catch (error) {
      console.error("Error creating discipline type:", error);
      return null;
    }
  };

  const createDeliverableType = async (name) => {
    try {
      const response = await api.post("/deliverable-types/", { name });
      const newType = response.data;
      setDeliverableTypes((prev) => [...prev, newType]);
      return newType;
    } catch (error) {
      console.error("Error creating deliverable type:", error);
      return null;
    }
  };

  const handleEditClick = (documentId) => {
    const document = documents.find((document) => document.id === documentId);

    setSelectedDocument(document);
    setOpenEditDocument(true);
    setName(document.name);
    setDescription(document.description);
    setDisciplineType(document.discipline?.id || "");
    setDeliverableType(document.documentType?.id || "");
    setDeliverableStatus(document.deliverableStatus?.id || "");
    setRevNumber(document.revNumber);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setFileName(selectedFile.name);
  };

  // Hide "Related Assets" entries that just point back at the asset this
  // modal is already scoped to (or one of its known ancestors, passed in via
  // navigation state) — redundant since the modal header/breadcrumb already
  // says that. Compares against the polymorphic `links` array's own
  // parent_type/parent_id, matching AssetDocuments.jsx.
  const excludeLink = (link) => {
    const type = link.parent_type?.toLowerCase();
    const id = String(link.parent_id);
    if (type === objectType?.toLowerCase() && id === String(object.id)) {
      return true;
    }
    if (
      equipmentId != null &&
      type === "equipment" &&
      id === String(equipmentId)
    ) {
      return true;
    }
    if (tierId != null && type === "tier" && id === String(tierId)) {
      return true;
    }
    return false;
  };

  const fetchLinks = React.useCallback(async () => {
    try {
      const res = await api.get(
        `/deliverable-links/?parent_type=${objectType}&parent_id=${object.id}`,
      );
      const data = Array.isArray(res.data)
        ? res.data
        : (res.data.results ?? []);
      setLinkedAssets(data);
    } catch (err) {
      console.error("Failed to fetch links:", err);
    }
  }, [api, objectType, object.id]);

  useEffect(() => {
    if (open) fetchLinks();
  }, [open, fetchLinks]);

  // ── Delink an asset ───────────────────────────────────────────────────────
  const handleDelink = async (deliverableId) => {
    // Find the junction record whose deliverable matches this document
    const link = linkedAssets.find((l) => l.deliverable === deliverableId);
    if (!link) {
      toast({
        title: "Error",
        description: "No link record found for this asset.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    setDelinking(link.id);
    try {
      await api.delete(`/deliverable-links/${link.id}/`);
      setLinkedAssets((prev) => prev.filter((l) => l.id !== link.id));
      toast({
        title: "Unlinked",
        description: "Asset removed from this document.",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to unlink asset.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setDelinking(null);
    }
  };

  const fetchOptions = React.useCallback(async () => {
    try {
      const [disciplineRes, deliverableTypeRes] = await Promise.all([
        api.get("/discipline-types/"),
        api.get("/deliverable-types/"),
      ]);
      setDisciplineTypes(disciplineRes.data.results);
      setDeliverableTypes(deliverableTypeRes.data.results);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  }, [api]);

  useEffect(() => {
    if (!open || (disciplineTypes.length && deliverableTypes.length)) return;
    fetchOptions();
  }, [disciplineTypes.length, deliverableTypes.length, fetchOptions, open]);

  const items = [
    {
      key: "1",
      label: "Open",
      onClick: () => {
        if (menuCard?.file) {
          window.open(menuCard.file_url, "_blank");
        } else {
          console.warn("No file URL found for this document.");
        }
        setAnchorEl(null); // Optional: close the menu after clicking
      },
    },
    canEdit && {
      key: "2",
      label: "Link Assets",
      onClick: () => {
        setLinkDocCard(menuCard); // ✅ capture which card to link
        setAnchorEl(null);
      },
    },
    canEdit && {
      key: "3",
      label: "Edit",
      onClick: () => {
        handleEditClick(menuCard.id);
      },
    },
    canEdit && {
      key: "4",
      label: "Delink",
      onClick: () => {
        handleDelink(menuCard.id); // passes deliverable ID, fn resolves link.id
      },
    },
    canDelete && {
      key: "5",
      label: "Delete",
      onClick: () => {
        handleDeleteClick(menuCard.id); // Remove the document
        setAnchorEl(null); // Close the menu
      },
    },
  ];

  const handleCardInteraction = (card) => {
    if (tapTimeout) {
      // Second tap within 300ms = double tap
      clearTimeout(tapTimeout);
      tapTimeout = null;

      // Open the file
      if (card.file) {
        window.open(card.file_url, "_blank");
      } else {
        console.warn("No file URL");
      }
    } else {
      // First tap - wait to see if another comes soon
      tapTimeout = setTimeout(() => {
        // Single tap action
        setSelectedCard(card);
        tapTimeout = null;
      }, 300);
    }
  };

  const updateDocInfo = async () => {
    setIsLoading(true);
    try {
      await schema.validate(
        { name, disciplineType, deliverableType, revNumber, description },
        { abortEarly: false },
      );

      const formField = new FormData();
      formField.append("name", name);
      formField.append("discipline_id", disciplineType); // ✅ ID + correct key
      formField.append("documentType_id", deliverableType); // ✅ ID + correct key
      formField.append("revNumber", revNumber);
      formField.append("description", description);

      if (file !== null) {
        formField.append("file", file);
      }

      await api({
        method: "PATCH",
        url: `/deliverables/${selectedDocument.id}/`,
        data: formField,
      });
      await fetchAssetDocuments();

      toast({
        title: "Success",
        description: "Document details updated successfully!",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      handleEditClose();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const newErrors = {};
        error.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
        setErrors(newErrors);
      } else {
        toast({
          title: "Error",
          description: "Failed to update document details.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClose = () => {
    setOpenEditDocument(false);
  };

  const handleDeleteClick = (id) => {
    setDeleteTarget({ id });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    try {
      const endpoint = `/deliverables/${id}/`;

      await api.delete(endpoint);

      setDocuments((prev) => prev.filter((e) => e.id !== id));

      toast({
        title: "Success",
        description: `Document deleted successfully!`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });

      setOpenDialog(false);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to delete document`,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const [deleteTarget, setDeleteTarget] = useState({ id: null, type: null });

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

  const fetchAssetDocuments = React.useCallback(async () => {
    setLoading(true);
    try {
      // Your existing approach - just pass objectType as query param
      const response = await api.get(
        `/deliverables/?${objectType}=${object.id}&has_file=true&ordering=-created`,
      );
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  }, [api, object.id, objectType]);

  useEffect(() => {
    if (!open) return;
    fetchAssetDocuments();
  }, [fetchAssetDocuments, open]);

  const filteredDocuments = documents.filter(
    (doc) =>
      doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Box container>
      <Typography
        onClick={handleOpen}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <FileOpen color="primary" /> <Typography>Documents</Typography>
      </Typography>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
        sx={{ zIndex: 1000 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "85%" },
            bgcolor: "background.paper",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
          }}
        >
          {/* ── Header ── */}
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: "primary.main",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Title */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#fff",
                letterSpacing: 0.3,
                textTransform: "uppercase",
                flexShrink: 0,
              }}
            >
              {objectType && `${object?.tagNumber}`} Docs
            </Typography>

            {/* Search */}
            <Box sx={{ flexGrow: 1, maxWidth: 360, minWidth: 200 }}>
              <Search
                placeholder="Search documents"
                allowClear
                enterButton
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={(value) => setSearchQuery(value)}
                style={{ width: "100%" }}
              />
            </Box>

            {/* Actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {canEdit && (
                <AddDocuments
                  object={objectType}
                  id={object?.id}
                  fetchData={fetchAssetDocuments}
                />
              )}
              <IconButton
                color={viewMode === "grid" ? "inherit" : "inherit"}
                onClick={() => setViewMode("grid")}
                sx={{
                  color: viewMode === "grid" ? "#fff" : "rgba(255,255,255,0.5)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                }}
              >
                <ViewModuleIcon />
              </IconButton>
              <IconButton
                disabled
                onClick={() => setViewMode("list")}
                sx={{
                  color: viewMode === "list" ? "#fff" : "rgba(255,255,255,0.3)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                }}
              >
                <ViewListIcon />
              </IconButton>
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  color: "#fff",
                  ml: 1,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                }}
              >
                <CloseOutlined />
              </IconButton>
            </Box>
          </Box>

          {/* ── Body: content + sider ── */}
          <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
            {/* ── Document grid ── */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                p: 2.5,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "grey.400",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "grey.600",
                },
                "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={64} />
                </Box>
              ) : documents.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "14px",
                    border: "1px dashed",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontStyle="italic"
                  >
                    There are no documents for this asset.
                  </Typography>
                </Box>
              ) : (
                Object.entries(
                  filteredDocuments.reduce((acc, doc) => {
                    const discipline = doc.discipline?.name;
                    if (!acc[discipline]) acc[discipline] = [];
                    acc[discipline].push(doc);
                    return acc;
                  }, {}),
                ).map(([discipline, docs]) => (
                  <Accordion
                    key={discipline}
                    defaultExpanded
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "12px !important",
                      overflow: "hidden",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                      sx={{
                        bgcolor: "primary.main",
                        minHeight: 56,
                        "& .MuiAccordionSummary-content": {
                          m: 0,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            color: "#fff",
                          }}
                        >
                          {discipline}
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: "rgba(255,255,255,0.25)",
                            color: "#fff",
                            borderRadius: "999px",
                            px: 1.1,
                            py: 0.15,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            lineHeight: 1.4,
                            minWidth: 24,
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          {docs.length}
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{ bgcolor: "background.paper", p: 2 }}
                    >
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {docs.map((card) => {
                          const fileName = card?.file || card?.name || "";
                          const ext = fileName.split(".").pop()?.toLowerCase();

                          const getFileStyle = (ext) => {
                            switch (ext) {
                              case "pdf":
                                return { bgColor: "#f44336", label: "PDF" };
                              case "doc":
                              case "docx":
                                return { bgColor: "#3f51b5", label: "DOC" };
                              case "xls":
                              case "xlsx":
                                return { bgColor: "#4caf50", label: "XLS" };
                              case "jpg":
                              case "jpeg":
                              case "png":
                                return { bgColor: "#ff9800", label: "IMG" };
                              default:
                                return {
                                  bgColor: "#9e9e9e",
                                  label: ext?.toUpperCase() || "FILE",
                                };
                            }
                          };

                          const { bgColor, label } = getFileStyle(ext);
                          const isSelected = selectedCard?.id === card.id;

                          return (
                            <Box
                              key={card.id}
                              onClick={() => handleCardInteraction(card)}
                              onDoubleClick={() => {
                                if (card.file) window.open(card.file, "_blank");
                              }}
                              sx={{
                                width: { xs: "100%", sm: "47%", md: "23%" },
                                height: 250,
                                borderRadius: "20px",
                                bgcolor: "background.paper",
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                border: "1px solid",
                                borderColor: isSelected
                                  ? "primary.main"
                                  : "divider",
                                boxShadow: isSelected
                                  ? "0 8px 32px rgba(0,0,0,0.15)"
                                  : "0 4px 16px rgba(0,0,0,0.06)",
                                transform: isSelected
                                  ? "translateY(-4px)"
                                  : "none",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                  transform: "translateY(-4px)",
                                  boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
                                  borderColor: "primary.main",
                                },
                              }}
                            >
                              {/* ── Coloured banner ── */}
                              <Box
                                sx={{
                                  height: 80,
                                  bgcolor: bgColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <Typography
                                  variant="h4"
                                  sx={{
                                    color: "#fff",
                                    fontWeight: 700,
                                    opacity: 0.9,
                                  }}
                                >
                                  {label}
                                </Typography>
                              </Box>

                              {/* ── Card body ── */}
                              <Box
                                sx={{
                                  px: 2,
                                  pt: 1.5,
                                  pb: 1,
                                  flexGrow: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="subtitle2"
                                      noWrap
                                      sx={{
                                        fontWeight: 700,
                                        color: "primary.main",
                                        pr: 1,
                                      }}
                                    >
                                      {card.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                    >
                                      {card.fileType}
                                    </Typography>
                                  </Box>
                                  <Dropdown
                                    menu={{ items }}
                                    trigger={["click"]}
                                    overlayStyle={{
                                      zIndex: 3000,
                                      width: "150px",
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAnchorEl(e.currentTarget);
                                        setMenuCard(card);
                                      }}
                                      sx={{
                                        flexShrink: 0,
                                        color: "text.secondary",
                                        "&:hover": { bgcolor: "action.hover" },
                                      }}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </Dropdown>
                                </Box>

                                {/* Deliverable type chip */}
                                {card.documentType && (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignSelf: "flex-start",
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: "6px",
                                      border: "1px solid",
                                      borderColor: "divider",
                                      bgcolor: "grey.50",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {card.documentType?.name}
                                    </Typography>
                                  </Box>
                                )}

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    lineHeight: 1.5,
                                    flexGrow: 1,
                                  }}
                                >
                                  {card.description ||
                                    "No description provided"}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>

            {/* ── File Details Sider ── */}
            <Box
              sx={{
                width: 260,
                flexShrink: 0,
                borderLeft: "1px solid",
                borderColor: "divider",
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                overflowY: "auto",
                p: 2,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "grey.400",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
              }}
            >
              <DocumentDetailsPanel
                document={selectedCard}
                excludeLink={excludeLink}
              />
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Modal for edit document */}
      <Modal
        open={openEditDocument}
        onClose={handleEditClose}
        sx={{ zIndex: 1000 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: 500 },
            bgcolor: "background.paper",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
          }}
        >
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              bgcolor: "primary.main",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
            >
              Update Document
            </Typography>
            <IconButton
              onClick={handleEditClose}
              size="small"
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Box>

          {/* ── Scrollable body ── */}
          <Box
            sx={{
              overflowY: "auto",
              flexGrow: 1,
              px: 3,
              py: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "grey.400",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "grey.600",
              },
              "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
            }}
          >
            {/* Drawing No. */}
            <TextField
              label="Drawing No."
              value={name}
              onChange={(e) => setName(e.target.value)}
              variant="outlined"
              fullWidth
              error={!!errors.name}
              helperText={errors.name}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            {/* File upload */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <Button
                icon={<UploadOutlined style={{ fontSize: "18px" }} />}
                onClick={handleUploadClick}
                style={{
                  backgroundColor: "#007DAB",
                  color: "white",
                  borderRadius: "8px",
                  border: "none",
                }}
              >
                Upload File
              </Button>
              {file && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {fileName}
                </Typography>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Box>

            {/* Description */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              fullWidth
              error={!!errors.description}
              helperText={errors.description}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            {/* Discipline Type */}
            <Autocomplete
              disablePortal
              id="disciplineType"
              fullWidth
              freeSolo
              options={disciplineTypes}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
              }
              filterOptions={(options, params) => {
                const filtered = options.filter((opt) =>
                  opt.name
                    .toLowerCase()
                    .includes(params.inputValue.toLowerCase()),
                );
                if (
                  params.inputValue !== "" &&
                  !options.some(
                    (opt) =>
                      opt.name.toLowerCase() ===
                      params.inputValue.toLowerCase(),
                  )
                ) {
                  filtered.push({
                    name: `Add "${params.inputValue}"`,
                    inputValue: params.inputValue,
                  });
                }
                return filtered;
              }}
              value={
                disciplineTypes.find((opt) => opt.id === disciplineType) || null
              }
              onChange={async (e, newValue) => {
                if (newValue?.inputValue) {
                  const created = await createDisciplineType(
                    newValue.inputValue,
                  );
                  if (created) setDisciplineType(created.id);
                } else {
                  setDisciplineType(newValue?.id || "");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Discipline Type"
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              )}
            />

            {/* Document Type */}
            <Autocomplete
              disablePortal
              id="deliverableType"
              fullWidth
              freeSolo
              options={deliverableTypes}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
              }
              filterOptions={(options, params) => {
                const filtered = options.filter((opt) =>
                  opt.name
                    .toLowerCase()
                    .includes(params.inputValue.toLowerCase()),
                );
                if (
                  params.inputValue !== "" &&
                  !options.some(
                    (opt) =>
                      opt.name.toLowerCase() ===
                      params.inputValue.toLowerCase(),
                  )
                ) {
                  filtered.push({
                    name: `Add "${params.inputValue}"`,
                    inputValue: params.inputValue,
                  });
                }
                return filtered;
              }}
              value={
                deliverableTypes.find((opt) => opt.id === deliverableType) ||
                null
              }
              onChange={async (e, newValue) => {
                if (newValue?.inputValue) {
                  const created = await createDeliverableType(
                    newValue.inputValue,
                  );
                  if (created) setDeliverableType(created.id);
                } else {
                  setDeliverableType(newValue?.id || "");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Document Type"
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              )}
            />

            {/* Revision Number */}
            <TextField
              label="Revision Number"
              value={revNumber}
              onChange={(e) => setRevNumber(e.target.value)}
              variant="outlined"
              fullWidth
              error={!!errors.revNumber}
              helperText={errors.revNumber}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            {/* ── Footer buttons ── */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 1,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleEditClose}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={updateDocInfo}
                disabled={isLoading}
                sx={{
                  bgcolor: "primary.main",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 140,
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Update Document"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCancelDelete}
        sx={{
          "& .MuiDialog-paper": {
            padding: 2,
            borderRadius: 3,
            boxShadow: 6,
            width: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: "bold", color: "error.main", textAlign: "center" }}
        >
          Delete Document
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: "1rem",
              color: "text.secondary",
              textAlign: "center",
              mb: 2,
            }}
          >
            Are you sure you want to delete this document?
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: "space-around", paddingBottom: 2 }}
        >
          <Button
            onClick={() => handleDelete(deleteTarget.id)}
            variant="contained"
            color="error"
            sx={{ fontWeight: "bold" }}
          >
            Delete
          </Button>
          <Button
            onClick={handleCancelDelete}
            variant="contained"
            sx={{
              backgroundColor: "#007DAB",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#005f80" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {linkDocCard && (
        <LinkDoc
          deliverableId={linkDocCard.id}
          externalOpen={true} // prop to auto-open parent modal
          onClose={() => setLinkDocCard(null)}
        />
      )}
    </Box>
  );
};

export { DocsModal };
